import { Gender, Grade, GradeMap } from '../job-posting'

const DAILY_WAGE = 180000
const GradeLtcMaxTimeMap = {
  [GradeMap.First]: 4,
  [GradeMap.Second]: 4,
  [GradeMap.Third]: 3,
  [GradeMap.Fourth]: 3,
  [GradeMap.Fifth]: 3,
  [GradeMap.None]: undefined,
  [GradeMap.Sixth]: undefined,
}

const GradeLtcMaxWorkingDaysMap = {
  // *공단에서 26일 이상은 등록이 안됨 -> 모두 가능 일수를 26일로 조정
  [GradeMap.First]: 26,
  [GradeMap.Second]: 26,
  [GradeMap.Third]: 26,
  [GradeMap.Fourth]: 25,
  [GradeMap.Fifth]: 21,
  [GradeMap.None]: undefined,
  [GradeMap.Sixth]: undefined,
}

const GradeLtcMaxMedicalCharge = {
  [GradeMap.First]: 2069900,
  [GradeMap.Second]: 1869600,
  [GradeMap.Third]: 1455800,
  [GradeMap.Fourth]: 1341800,
  [GradeMap.Fifth]: 1151600,
  [GradeMap.None]: undefined,
  [GradeMap.Sixth]: undefined,
}

const LTC_GRADE_DANGER_DAILY_BONUS = 3000

const commuteNoneLtcWageTable: Record<string, Record<string, number[]>> = {
  first: {
    female: [18000, 16000, 14000],
    male: [19000, 16500, 14500],
  },
  second: {
    female: [18000, 16000, 14000],
    male: [19000, 16500, 14500],
  },
  third: {
    female: [16000, 14500, 13500],
    male: [17000, 15000, 13800],
  },
  fourth: {
    female: [16000, 14500, 13500],
    male: [17000, 15000, 13800],
  },
  fifth: {
    female: [17000, 15000, 14000],
    male: [17500, 15500, 14000],
  },
  sixth: {
    female: [16000, 14500, 13300],
    male: [16500, 15000, 14000],
  },
  none: {
    female: [16000, 14500, 13300],
    male: [16500, 15000, 14000],
  },
}

const residentNoneLtcSalaryTable: Record<string, Record<string, number>> = {
  first: {
    female: 3500000,
    male: 3500000,
  },
  second: {
    female: 3500000,
    male: 3500000,
  },
  third: {
    female: 3400000,
    male: 3500000,
  },
  fourth: {
    female: 3400000,
    male: 3400000,
  },
  fifth: {
    female: 3500000,
    male: 3550000,
  },
  sixth: {
    female: 3400000,
    male: 3500000,
  },
  none: {
    female: 3400000,
    male: 3500000,
  },
}

export type WageInfo = {
  ltcBenefit: number
  ltcSelfLevy: number
  noneLtcWage: number
  monthlyWorkingDay: number
}

export function getCommuteWage(
  includeSunday: boolean,
  _grade: Grade,
  gender: Gender,
  requestTime: number | null,
  numberOfPerWeekWorkingDays: number | null
): WageInfo {
  const grade = (_grade as string) === 'unknown' ? 'none' : _grade
  const monthlyWorkingDay = getMonthlyWorkingDay(numberOfPerWeekWorkingDays)
  const maxLtcWorkingDays = GradeLtcMaxWorkingDaysMap[grade]
    ? Math.min(GradeLtcMaxWorkingDaysMap[grade] as number, monthlyWorkingDay)
    : monthlyWorkingDay
  const ltcServiceWageInfo = getCommuteLtcWageAndWorkingDays(
    grade,
    requestTime ?? 0,
    maxLtcWorkingDays
  )
  const maxNoneLtcTime = GradeLtcMaxTimeMap[grade]
    ? Math.min(GradeLtcMaxTimeMap[grade] as number, requestTime ?? 0)
    : requestTime ?? 0
  const noneLtcWage = getHourlyWage(gender, grade, requestTime ?? 0)
  // 10시간 초과는 입주대근으로, 안내
  const noneLtcServiceWageInfo =
    (requestTime ?? 0) > 10
      ? over10CommuteCharge(
          includeSunday,
          numberOfPerWeekWorkingDays ?? 0,
          ltcServiceWageInfo.ltcSelfLevy,
          gender,
          grade
        )
      : getCommuteNoneLtcCharge(
          grade,
          monthlyWorkingDay,
          requestTime ?? 0,
          ltcServiceWageInfo.workingDays,
          maxNoneLtcTime,
          noneLtcWage
        )

  return {
    ltcBenefit: Math.round(ltcServiceWageInfo.ltcTotalAmount - ltcServiceWageInfo.ltcSelfLevy),
    ltcSelfLevy: Math.round(ltcServiceWageInfo.ltcSelfLevy),
    noneLtcWage: Math.round(noneLtcServiceWageInfo),
    monthlyWorkingDay: monthlyWorkingDay,
  }
}

function getMonthlyWorkingDay(numberOfPerWeekWorkingDays: number | null): number {
  // 7이라는 한계를 알려줌
  if (
    numberOfPerWeekWorkingDays === null ||
    numberOfPerWeekWorkingDays > 7 ||
    numberOfPerWeekWorkingDays < 1
  ) {
    throw new Error('numberOfPerWeekWorkingDays는 1부터 7 사이의 값이어야 합니다.')
  }

  // 6에서 3까지는 고정된 값을 반환
  const fixedValues: { [key: number]: number } = {
    7: 30,
    6: 26,
    5: 22,
    4: 17,
    3: 13,
    2: 8,
    1: 4,
  }
  return fixedValues[numberOfPerWeekWorkingDays]
}

function getCommuteLtcWageAndWorkingDays(
  grade: Grade,
  requestTime: number,
  maxLtcWorkingDays: number
): { ltcTotalAmount: number; ltcSelfLevy: number; workingDays: number } {
  const medicalCharge = getLtcGradeMedicalCharge(grade, requestTime)
  const maxMedicalCharge = GradeLtcMaxMedicalCharge[grade] ?? Number.MAX_SAFE_INTEGER
  const adjustedMaxWorkingDays = Math.floor(maxMedicalCharge / medicalCharge)
  const workingDays = Math.min(maxLtcWorkingDays, adjustedMaxWorkingDays)

  return {
    ltcTotalAmount: medicalCharge * workingDays,
    ltcSelfLevy: medicalCharge * workingDays * 0.15,
    workingDays: workingDays,
  }
}

function getLtcGradeMedicalCharge(grade: Grade, requestTime: number) {
  if (([GradeMap.None, GradeMap.Sixth] as string[]).includes(grade)) return 0
  const maxTime = GradeLtcMaxTimeMap[grade]
    ? Math.min(GradeLtcMaxTimeMap[grade] as number, requestTime)
    : requestTime

  switch (maxTime) {
    case 4:
      return 66770
    case 3:
      return 54320
    case 2:
      return 41380
    default:
      return 3618
  }
}

function over10CommuteCharge(
  includeSunday: boolean,
  numberOfPerWeekWorkingDays: number,
  ltcSelfLevy: number,
  gender: string,
  grade: string
) {
  // 2일 이하, 3일 이상
  const monthlyWorkingDay = getMonthlyWorkingDay(numberOfPerWeekWorkingDays)
  if (numberOfPerWeekWorkingDays <= 2) {
    return monthlyWorkingDay * DAILY_WAGE - ltcSelfLevy
  } else {
    return getResidentCharge(
      includeSunday,
      grade as Grade,
      gender as Gender,
      numberOfPerWeekWorkingDays,
      undefined
    ).noneLtcWage
  }
}

// 비급여 부담 계산
function getCommuteNoneLtcCharge(
  grade: Grade,
  monthlyWorkingDay: number,
  requestTime: number,
  ltcWorkingDays: number,
  ltcTime: number,
  wage: number
) {
  const totalTimes = ([GradeMap.None, GradeMap.Sixth] as string[]).includes(grade)
    ? monthlyWorkingDay * requestTime
    : monthlyWorkingDay * requestTime - ltcWorkingDays * ltcTime

  return totalTimes * wage
}

function getHourlyWage(gender: Gender, grade: Grade, hours: number): number {
  if (hours <= 8 && 0 <= hours) {
    return commuteNoneLtcWageTable[grade][gender][0]
  } else if (hours === 9) {
    return commuteNoneLtcWageTable[grade][gender][1]
  } else if (hours === 10) {
    return commuteNoneLtcWageTable[grade][gender][2]
  } else if (hours > 10) {
    return hours
  } else {
    throw new Error('Invalid number of hours')
  }
}

function carePartnerProfit(numberOfPerWeekWorkingDays: number) {
  return (300000 / 6) * numberOfPerWeekWorkingDays
}

export function getResidentCharge(
  includeSunday: boolean,
  _grade: Grade,
  gender: Gender,
  numberOfPerWeekWorkingDays: number | null,
  numberOfRiskServices?: number
): WageInfo {
  const maxRiskServicePrice =
    (numberOfPerWeekWorkingDays ?? 0) <= 2
      ? Math.min(15000, (numberOfRiskServices ?? 0) * 5000)
      : Math.min(300000, (numberOfRiskServices ?? 0) * 100000)
  const grade = (_grade as string) === 'unknown' ? 'none' : _grade
  const monthlyWorkingDay = getMonthlyWorkingDay(numberOfPerWeekWorkingDays)
  const workingSundaysPerMonth = includeSunday ? 4 : 0
  const medicalCharge = getLtcGradeMedicalCharge(grade, 24)
  const initialLtcWorkingDays = GradeLtcMaxWorkingDaysMap[grade]
    ? Math.min(monthlyWorkingDay, GradeLtcMaxWorkingDaysMap[grade] as number)
    : monthlyWorkingDay

  // Calculate the initial ltcCharge
  const initialLtcCharge =
    (initialLtcWorkingDays - workingSundaysPerMonth) * medicalCharge +
    workingSundaysPerMonth * 1.3 * medicalCharge
  const ltcMaxMedicalCharge = GradeLtcMaxMedicalCharge[grade] ?? 0

  // Find the maximum ltcWorkingDays that doesn't exceed ltcMaxMedicalCharge
  let ltcWorkingDays = initialLtcWorkingDays
  let ltcCharge = initialLtcCharge

  if (ltcCharge > ltcMaxMedicalCharge) {
    while (ltcWorkingDays > 0) {
      ltcWorkingDays--
      ltcCharge =
        (ltcWorkingDays - workingSundaysPerMonth) * medicalCharge +
        workingSundaysPerMonth * 1.3 * medicalCharge
      if (ltcCharge <= ltcMaxMedicalCharge) {
        break
      }
    }
  }

  const ltcTotalAmount = Math.min(ltcCharge, ltcMaxMedicalCharge)
  const ltcSalary = ltcWorkingDays * 12500 * (GradeLtcMaxTimeMap[grade] ?? 0)
  const noneLtcCharge =
    (numberOfPerWeekWorkingDays ?? 0) <= 2
      ? monthlyWorkingDay * (DAILY_WAGE + maxRiskServicePrice)
      : ((residentNoneLtcSalaryTable[grade][gender] + maxRiskServicePrice) / 26) * monthlyWorkingDay

  const dangerBonus =
    grade === GradeMap.First || grade === GradeMap.Second
      ? LTC_GRADE_DANGER_DAILY_BONUS * ltcWorkingDays
      : 0
  const noneLtcChargeSelfLevy =
    noneLtcCharge -
    ltcSalary -
    dangerBonus +
    carePartnerProfit(numberOfPerWeekWorkingDays ?? 0) +
    (ltcCharge > ltcMaxMedicalCharge ? ltcCharge - ltcMaxMedicalCharge : 0)

  return {
    ltcBenefit: Math.round(ltcTotalAmount * 0.85),
    ltcSelfLevy: Math.round(ltcTotalAmount * 0.15),
    noneLtcWage: Math.round(noneLtcChargeSelfLevy),
    monthlyWorkingDay: monthlyWorkingDay,
  }
}
